import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Button,
	Card,
	Collapse,
	Descriptions,
	Form,
	Input,
	InputNumber,
	message,
	Popconfirm,
	Progress,
	Radio,
	Select,
	Space,
	Tag,
} from 'antd';
import useInterval from '../../hooks/useInterval';
import { roundOffToDecimal } from '../../utils/DataAccessUtils';
import {
	adjustWalkthroughSyncParallelism,
	cancelWalkthroughOpenSearchSync,
	fetchWalkthroughOpenSearchSyncStatus,
	triggerWalkthroughOpenSearchSync,
	WalkthroughSyncScope,
} from '../../thunks/walkthroughs_thunks';
import {
	adjustWorkOrderSyncParallelism,
	cancelWorkOrderOpenSearchSync,
	fetchWorkOrderOpenSearchSyncStatus,
	triggerWorkOrderOpenSearchSync,
	WorkOrderSyncScope,
} from '../../thunks/work_orders_thunks';

type SyncScopeKey = 'ids' | 'buyerCompanyIds' | 'syncAll';
type EntityKey = 'workorders' | 'walkthroughs';

type EntityConfig = {
	label: string;
	disabled: boolean;
	noun: string;
	idsLabel: string;
	idsRadioLabel: string;
	idsHelperText: string;
	idsPlaceholder: string;
	syncAllHelperText: string;
	noticeDescription: string;
	// Backend expects different scope keys per entity (workorders use `ids`,
	// walkthroughs use `walkthroughIds`).
	idsScopeKeyApi: 'ids' | 'walkthroughIds';
	trigger: (scope: any) => () => Promise<any>;
	status: () => () => Promise<any>;
	adjust: (auditId: number, parallelism: number) => () => Promise<any>;
	cancel: (auditId: number) => () => Promise<any>;
};

const ENTITY_CONFIGS: Record<EntityKey, EntityConfig> = {
	workorders: {
		label: 'Work Orders (coming soon)',
		disabled: true,
		noun: 'work orders',
		idsLabel: 'Work Order IDs',
		idsRadioLabel: 'By work order ID(s)',
		idsHelperText: 'Comma or space separated work order IDs (e.g. 101, 102, 103)',
		idsPlaceholder: '101, 102, 103',
		syncAllHelperText: 'All work orders across every buyer company will be re-indexed.',
		noticeDescription:
			'Triggers a re-index of selected work orders into OpenSearch. Only one work order sync can run at a time — new requests are rejected while one is in progress.',
		idsScopeKeyApi: 'ids',
		trigger: triggerWorkOrderOpenSearchSync,
		status: fetchWorkOrderOpenSearchSyncStatus,
		adjust: adjustWorkOrderSyncParallelism,
		cancel: cancelWorkOrderOpenSearchSync,
	},
	walkthroughs: {
		label: 'Walkthroughs',
		disabled: false,
		noun: 'walkthroughs',
		idsLabel: 'Walkthrough IDs',
		idsRadioLabel: 'By walkthrough ID(s)',
		idsHelperText: 'Comma or space separated walkthrough IDs (e.g. 101, 102, 103)',
		idsPlaceholder: '101, 102, 103',
		syncAllHelperText: 'All walkthroughs across every buyer company will be re-indexed.',
		noticeDescription:
			'Triggers a re-index of selected walkthroughs into OpenSearch. Only one walkthrough sync can run at a time — new requests are rejected while one is in progress.',
		idsScopeKeyApi: 'walkthroughIds',
		trigger: triggerWalkthroughOpenSearchSync,
		status: fetchWalkthroughOpenSearchSyncStatus,
		adjust: adjustWalkthroughSyncParallelism,
		cancel: cancelWalkthroughOpenSearchSync,
	},
};

const ENTITY_OPTIONS = (Object.keys(ENTITY_CONFIGS) as EntityKey[]).map((value) => ({
	value,
	label: ENTITY_CONFIGS[value].label,
	disabled: ENTITY_CONFIGS[value].disabled,
}));

const DEFAULT_PARALLELISM = 1;
const DEFAULT_BATCH_SIZE = 50;
const MAX_PARALLELISM = 16;
const MAX_BATCH_SIZE = 500;
const STATUS_POLL_INTERVAL_MS = 3000;
const ETA_WINDOW_MS = 30_000;

type ActiveAudit = {
	id: number;
	totalRecords: number;
	completedRecords: number;
	isRunning: boolean;
	createdAt?: string;
	createdBy?: string;
	desiredParallelism?: number;
	cancelRequestedAt?: string;
	errorMessages?: string[];
	batchesCompleted?: number;
	lastBatchDurationMs?: number;
	avgBatchDurationMs?: number;
	avgDbTimeMs?: number;
	avgOsTimeMs?: number;
	avgPermitWaitMs?: number;
};

type ThroughputSample = { timestamp: number; completed: number };

const formatDuration = (seconds: number): string => {
	if (!isFinite(seconds) || seconds < 0) return '—';
	if (seconds < 1) return '<1s';
	const totalSeconds = Math.round(seconds);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const remainderSeconds = totalSeconds % 60;
	if (hours > 0) return `${hours}h ${minutes}m`;
	if (minutes > 0) return `${minutes}m ${remainderSeconds}s`;
	return `${remainderSeconds}s`;
};

const parseCsvIds = (raw: string): string =>
	raw
		.split(/[\s,]+/)
		.map((segment) => segment.trim())
		.filter(Boolean)
		.join(',');

const OpenSearchSyncPage: FC<any> = () => {
	const [entity, setEntity] = useState<EntityKey>('walkthroughs');
	const entityConfig = ENTITY_CONFIGS[entity];

	const [scope, setScope] = useState<SyncScopeKey>('ids');
	const [idsInput, setIdsInput] = useState<string>('');
	const [parallelism, setParallelism] = useState<number>(DEFAULT_PARALLELISM);
	const [batchSize, setBatchSize] = useState<number>(DEFAULT_BATCH_SIZE);
	const [submitting, setSubmitting] = useState<boolean>(false);

	const [activeAudit, setActiveAudit] = useState<ActiveAudit | null>(null);
	const [adjustValue, setAdjustValue] = useState<number>(DEFAULT_PARALLELISM);
	const [adjusting, setAdjusting] = useState<boolean>(false);
	const [cancelling, setCancelling] = useState<boolean>(false);

	const samplesRef = useRef<ThroughputSample[]>([]);
	const lastAuditIdRef = useRef<number | null>(null);

	const helperText = useMemo(() => {
		switch (scope) {
			case 'ids':
				return entityConfig.idsHelperText;
			case 'buyerCompanyIds':
				return 'Comma or space separated buyer company IDs (e.g. 1, 4, 7)';
			case 'syncAll':
				return entityConfig.syncAllHelperText;
		}
	}, [scope, entityConfig]);

	const buildScope = useCallback((): WalkthroughSyncScope | WorkOrderSyncScope | null => {
		const base = { parallelism, batchSize };
		if (scope === 'syncAll') return { ...base, syncAll: true };
		const ids = parseCsvIds(idsInput);
		if (!ids) return null;
		if (scope === 'buyerCompanyIds') return { ...base, buyerCompanyIds: ids };
		return { ...base, [entityConfig.idsScopeKeyApi]: ids } as
			| WalkthroughSyncScope
			| WorkOrderSyncScope;
	}, [idsInput, scope, parallelism, batchSize, entityConfig]);

	const refreshStatus = useCallback(() => {
		(entityConfig.status() as any)()
			.then((audits: ActiveAudit[]) => {
				const running = audits && audits.length > 0 ? audits[0] : null;
				setActiveAudit(running);
				if (running) {
					const isNewAudit = lastAuditIdRef.current !== running.id;
					if (running.desiredParallelism) {
						setAdjustValue((prev) =>
							isNewAudit || prev === DEFAULT_PARALLELISM ? running.desiredParallelism! : prev
						);
					}
					if (isNewAudit) {
						samplesRef.current = [];
						lastAuditIdRef.current = running.id;
					}
					const now = Date.now();
					samplesRef.current.push({ timestamp: now, completed: running.completedRecords });
					samplesRef.current = samplesRef.current.filter(
						(sample) => now - sample.timestamp <= ETA_WINDOW_MS
					);
				} else {
					samplesRef.current = [];
					lastAuditIdRef.current = null;
				}
			})
			.catch(() => {
				/* keep previous state on transient error */
			});
	}, [entityConfig]);

	// Reset transient sync state and re-poll when the entity selector changes.
	useEffect(() => {
		setActiveAudit(null);
		setIdsInput('');
		samplesRef.current = [];
		lastAuditIdRef.current = null;
		refreshStatus();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [entity]);

	useInterval(refreshStatus, STATUS_POLL_INTERVAL_MS);

	const onSubmit = useCallback(() => {
		const body = buildScope();
		if (!body) {
			message.error('Enter at least one ID.');
			return;
		}
		setSubmitting(true);
		(entityConfig.trigger(body) as any)()
			.then((data) => {
				message.success(typeof data === 'string' ? data : 'Sync started.');
				refreshStatus();
			})
			.catch((err) => message.error(err || 'Failed to start sync.'))
			.finally(() => setSubmitting(false));
	}, [buildScope, refreshStatus, entityConfig]);

	const onAdjustParallelism = useCallback(() => {
		if (!activeAudit) return;
		setAdjusting(true);
		(entityConfig.adjust(activeAudit.id, adjustValue) as any)()
			.then((data) => {
				message.success(
					typeof data === 'string' ? data : `Parallelism adjusted to ${adjustValue}.`
				);
				refreshStatus();
			})
			.catch((err) => message.error(err || 'Failed to adjust parallelism.'))
			.finally(() => setAdjusting(false));
	}, [activeAudit, adjustValue, refreshStatus, entityConfig]);

	const onCancel = useCallback(() => {
		if (!activeAudit) return;
		setCancelling(true);
		(entityConfig.cancel(activeAudit.id) as any)()
			.then((data) => {
				message.success(typeof data === 'string' ? data : 'Cancel requested.');
				refreshStatus();
			})
			.catch((err) => message.error(err || 'Failed to cancel sync.'))
			.finally(() => setCancelling(false));
	}, [activeAudit, refreshStatus, entityConfig]);

	const progressPercent = useMemo(() => {
		if (!activeAudit || !activeAudit.totalRecords) return 0;
		return roundOffToDecimal((activeAudit.completedRecords * 100) / activeAudit.totalRecords);
	}, [activeAudit]);

	const syncInProgress = !!(activeAudit && activeAudit.isRunning);
	const cancelRequested = !!(activeAudit && activeAudit.cancelRequestedAt);

	// Rolling-window throughput is more accurate than backend avg because the backend average
	// includes the slow ramp-up at parallelism=1; the rolling slope reflects current settings.
	const { recordsPerSec, etaSeconds } = useMemo(() => {
		if (!activeAudit || !activeAudit.isRunning) {
			return { recordsPerSec: null as number | null, etaSeconds: null as number | null };
		}
		const samples = samplesRef.current;
		if (samples.length < 2) return { recordsPerSec: null, etaSeconds: null };
		const oldest = samples[0];
		const newest = samples[samples.length - 1];
		const elapsedSec = (newest.timestamp - oldest.timestamp) / 1000;
		if (elapsedSec <= 0) return { recordsPerSec: null, etaSeconds: null };
		const rate = (newest.completed - oldest.completed) / elapsedSec;
		if (rate <= 0) return { recordsPerSec: 0, etaSeconds: null };
		const remaining = Math.max(0, activeAudit.totalRecords - activeAudit.completedRecords);
		return { recordsPerSec: rate, etaSeconds: remaining / rate };
	}, [activeAudit]);

	const recommendation = useMemo(() => {
		if (!activeAudit || !activeAudit.isRunning) return null;
		const batchMs = activeAudit.avgBatchDurationMs || 0;
		const waitMs = activeAudit.avgPermitWaitMs || 0;
		const dbMs = activeAudit.avgDbTimeMs || 0;
		const osMs = activeAudit.avgOsTimeMs || 0;
		const parallelism = activeAudit.desiredParallelism || DEFAULT_PARALLELISM;
		// Need at least a few batches before recommending.
		if (!activeAudit.batchesCompleted || activeAudit.batchesCompleted < 3) return null;

		if (batchMs > 0 && waitMs > 0.3 * batchMs && parallelism < MAX_PARALLELISM) {
			return {
				type: 'warning' as const,
				message: 'Workers waiting for permits',
				description:
					`Permit-wait is ${Math.round(
						(waitMs / batchMs) * 100
					)}% of batch time — downstream isn't saturated. ` +
					`Increasing parallelism (currently ${parallelism}) should improve throughput.`,
			};
		}
		if (batchMs > 0 && waitMs < 0.05 * batchMs && dbMs > 2 * osMs) {
			return {
				type: 'error' as const,
				message: 'DB-bound — be careful raising parallelism',
				description:
					`MySQL hydration is the bottleneck (DB ${dbMs}ms vs OS ${osMs}ms per batch). ` +
					`Raising parallelism will further load MySQL. Consider lowering parallelism or shrinking batch size.`,
			};
		}
		if (batchMs > 0 && waitMs < 0.05 * batchMs && osMs > 2 * dbMs) {
			return {
				type: 'info' as const,
				message: 'OpenSearch-bound — try a larger batch size',
				description:
					`OpenSearch bulk write dominates (OS ${osMs}ms vs DB ${dbMs}ms per batch). ` +
					`Larger batches typically index more efficiently than more concurrent small batches.`,
			};
		}
		return {
			type: 'success' as const,
			message: 'Balanced',
			description: 'DB, OpenSearch, and parallelism look healthy with current settings.',
		};
	}, [activeAudit]);

	const handleScopeChange = useCallback((event) => {
		setScope(event.target.value);
		setIdsInput('');
	}, []);

	const batchSizeExtra = `${
		entityConfig.noun.charAt(0).toUpperCase() + entityConfig.noun.slice(1)
	} indexed per batch (1 – ${MAX_BATCH_SIZE}).`;

	return (
		<div className="p-6">
			<Card title="OpenSearch Sync" bordered={false}>
				<Alert
					className="mb-6"
					type="warning"
					showIcon
					message="Super Admin Only"
					description={entityConfig.noticeDescription}
				/>

				<Form layout="vertical" className="max-w-[640px]">
					<Form.Item label="Entity">
						<Select
							value={entity}
							onChange={setEntity}
							options={ENTITY_OPTIONS}
							disabled={submitting || syncInProgress}
						/>
					</Form.Item>

					<Form.Item label="Sync scope">
						<Radio.Group
							value={scope}
							onChange={handleScopeChange}
							disabled={submitting || syncInProgress}
						>
							<Radio value="ids">{entityConfig.idsRadioLabel}</Radio>
							<Radio value="buyerCompanyIds">By buyer company ID(s)</Radio>
							<Radio value="syncAll">Sync all</Radio>
						</Radio.Group>
					</Form.Item>

					{scope !== 'syncAll' ? (
						<Form.Item
							label={scope === 'ids' ? entityConfig.idsLabel : 'Buyer Company IDs'}
							extra={helperText}
						>
							<Input.TextArea
								rows={3}
								value={idsInput}
								onChange={(e) => setIdsInput(e.target.value)}
								placeholder={scope === 'ids' ? entityConfig.idsPlaceholder : '1, 4, 7'}
								disabled={submitting || syncInProgress}
							/>
						</Form.Item>
					) : (
						<Alert type="info" showIcon message={helperText} className="mb-6" />
					)}

					{/* Parallelism and batch size are locked to their defaults for now — the inputs are
					    disabled so a sync always runs with the safe default tuning. */}
					<Form.Item
						label="Parallelism"
						extra={`Concurrent batches processed in parallel (1 – ${MAX_PARALLELISM}).`}
					>
						<InputNumber
							min={1}
							max={MAX_PARALLELISM}
							value={parallelism}
							onChange={(value) => setParallelism(Number(value) || DEFAULT_PARALLELISM)}
							disabled
						/>
					</Form.Item>

					<Form.Item label="Batch size" extra={batchSizeExtra}>
						<InputNumber
							min={1}
							max={MAX_BATCH_SIZE}
							value={batchSize}
							onChange={(value) => setBatchSize(Number(value) || DEFAULT_BATCH_SIZE)}
							disabled
						/>
					</Form.Item>

					<Form.Item>
						<Button
							type="primary"
							size="large"
							loading={submitting}
							disabled={submitting || syncInProgress}
							onClick={onSubmit}
						>
							Start Sync
						</Button>
					</Form.Item>
				</Form>

				{activeAudit ? (
					<Card
						type="inner"
						title={
							<Space>
								<span>Active Sync</span>
								<Tag color={cancelRequested ? 'red' : syncInProgress ? 'processing' : 'success'}>
									{cancelRequested ? 'Cancel Requested' : syncInProgress ? 'Running' : 'Completed'}
								</Tag>
							</Space>
						}
						className="mt-6"
					>
						<Descriptions size="small" column={2} bordered className="mb-4">
							<Descriptions.Item label="Audit ID">{activeAudit.id}</Descriptions.Item>
							<Descriptions.Item label="Started By">
								{activeAudit.createdBy || '—'}
							</Descriptions.Item>
							<Descriptions.Item label="Total">{activeAudit.totalRecords}</Descriptions.Item>
							<Descriptions.Item label="Completed">
								{activeAudit.completedRecords}
							</Descriptions.Item>
							<Descriptions.Item label="Current Parallelism">
								{activeAudit.desiredParallelism != null
									? activeAudit.desiredParallelism
									: 'default'}
							</Descriptions.Item>
							<Descriptions.Item label="Cancel Requested At">
								{activeAudit.cancelRequestedAt || '—'}
							</Descriptions.Item>
							<Descriptions.Item label="ETA">
								{etaSeconds != null ? `~${formatDuration(etaSeconds)} remaining` : '—'}
							</Descriptions.Item>
							<Descriptions.Item label="Throughput">
								{recordsPerSec != null ? `${roundOffToDecimal(recordsPerSec)} records/sec` : '—'}
							</Descriptions.Item>
							<Descriptions.Item label="Batch duration" span={2}>
								{activeAudit.avgBatchDurationMs != null ? (
									<Space size="small" wrap>
										<Tag>last: {activeAudit.lastBatchDurationMs || 0}ms</Tag>
										<Tag>avg: {activeAudit.avgBatchDurationMs}ms</Tag>
										<Tag color="blue">DB: {activeAudit.avgDbTimeMs || 0}ms</Tag>
										<Tag color="purple">OS: {activeAudit.avgOsTimeMs || 0}ms</Tag>
										<Tag color="orange">Wait: {activeAudit.avgPermitWaitMs || 0}ms</Tag>
										<Tag>batches: {activeAudit.batchesCompleted || 0}</Tag>
									</Space>
								) : (
									'—'
								)}
							</Descriptions.Item>
						</Descriptions>

						<Progress
							percent={progressPercent}
							status={cancelRequested ? 'exception' : syncInProgress ? 'active' : 'success'}
						/>

						{recommendation ? (
							<Alert
								className="mt-4"
								type={recommendation.type}
								showIcon
								message={recommendation.message}
								description={recommendation.description}
							/>
						) : null}

						<Form layout="inline" className="mt-4">
							<Form.Item label="Adjust parallelism">
								<InputNumber
									min={1}
									max={MAX_PARALLELISM}
									value={adjustValue}
									onChange={(value) => setAdjustValue(Number(value) || DEFAULT_PARALLELISM)}
									disabled={adjusting || cancelRequested}
								/>
							</Form.Item>
							<Form.Item>
								<Button
									type="primary"
									onClick={onAdjustParallelism}
									loading={adjusting}
									disabled={cancelRequested}
								>
									Apply
								</Button>
							</Form.Item>
							<Form.Item>
								<Popconfirm
									title="Cancel this sync? In-flight batches will finish; no new batches will start."
									onConfirm={onCancel}
									okText="Yes, cancel"
									cancelText="No"
									disabled={cancelRequested}
								>
									<Button danger loading={cancelling} disabled={cancelRequested}>
										Cancel Sync
									</Button>
								</Popconfirm>
							</Form.Item>
						</Form>

						{activeAudit.errorMessages && activeAudit.errorMessages.length > 0 ? (
							<Collapse className="mt-4">
								<Collapse.Panel
									header={`Errors (${activeAudit.errorMessages.length})`}
									key="errors"
								>
									<pre className="m-0 max-h-60 overflow-auto">
										{activeAudit.errorMessages.join('\n')}
									</pre>
								</Collapse.Panel>
							</Collapse>
						) : null}
					</Card>
				) : null}
			</Card>
		</div>
	);
};

export default OpenSearchSyncPage;
